import { FAMILY_LABELS } from '../../../utils/tellraw';
import type { Compat, VersionFamily } from '../../../utils/tellraw/types';
import { cn } from '../../../utils/cn';

interface CompatTogglesProps {
  value: Compat;
  onChange: (next: Compat) => void;
  disabled?: boolean;
  className?: string;
}

const FAMILIES: VersionFamily[] = ['legacy', 'modern', 'current'];

/**
 * Which Minecraft versions the command has to work on.
 *
 * One command goes to every targeted server, so each family switched on narrows
 * what the message may use: 1.15 and older has no hex colours, and 1.21.5
 * renamed click and hover events. The last family cannot be switched off, since
 * a command that works nowhere is not a choice anyone means to make.
 */
export const CompatToggles = ({ value, onChange, disabled = false, className }: CompatTogglesProps) => {
  const onCount = FAMILIES.filter((f) => value[f]).length;
  return (
    <fieldset className={cn('flex flex-wrap items-center gap-x-4 gap-y-2', className)} disabled={disabled}>
      <legend className="sr-only">Works on</legend>
      <span className="text-xs font-medium text-glass-400" aria-hidden="true">Works on</span>
      {FAMILIES.map((family) => {
        const locked = value[family] && onCount === 1;
        return (
          <label
            key={family}
            className={cn('inline-flex items-center gap-2 text-xs text-glass-300', locked ? 'cursor-not-allowed' : 'cursor-pointer')}
            title={locked ? 'At least one version range has to stay on.' : undefined}
          >
            <input
              type="checkbox"
              className="form-checkbox"
              checked={value[family]}
              disabled={disabled || locked}
              onChange={(e) => onChange({ ...value, [family]: e.target.checked })}
            />
            <span className="tabular-nums">{FAMILY_LABELS[family]}</span>
          </label>
        );
      })}
    </fieldset>
  );
};
